import React, { useEffect, useState } from "react";
import {
  FaHandPointer,
  FaLock,
  FaLockOpen,
  FaInfoCircle,
} from "react-icons/fa";
import { Modal } from "../../shared";
import useAuth from "../../../Hooks/useAuth";
import { useAlert } from "../../../context/AlertContext";
import {
  useSendRemoteControlCommandMutation,
  useGetRemoteControlCommandsQuery,
} from "../../../store/storageRoles/devicesApi";
import logger from "../../../utils/logger";

// Espresso (Kalerm / Coffee Cloud) uzaktan kontrol komutlari.
// Doc: Coffee Cloud Platform API 2.0.8 — 2.13 / Appendix 3.6.
// Not: API mevcut kilit DURUMUNU dondurmuyor (sadece 2.13 issuance), bu yuzden
// her fonksiyon icin ayri Kilitle / Aç butonu var; aktif durum gosterilmez.
const FUNCTIONS = [
  {
    key: "deviceClick",
    label: "Ekran (Dokunmatik)",
    description: "Cihaz ekranına dokunmayı kilitler / açar.",
    icon: FaHandPointer,
    lock: "lockDeviceClick",
    unlock: "unLockDeviceClick",
  },
];

export default function RemoteControlTab({ selectedDevice }) {
  const { auth } = useAuth();
  const { showSuccess, showError } = useAlert();
  const [sendCommand, { isLoading }] = useSendRemoteControlCommandMutation();

  // O an gonderilen komut (buton bazli spinner icin) ve onay modali state'i.
  const [pendingCommand, setPendingCommand] = useState(null);
  const [confirm, setConfirm] = useState(null); // { command, title, message }

  const serialNo = selectedDevice?.serialNo || "";

  // 2.12 Remote Control-instruction Query — tab açılışında cihazın desteklediği
  // komutları çek (server kendi log'una basar).
  const { error: cmdError } = useGetRemoteControlCommandsQuery(
    { token: auth?.accessToken, serialNo },
    { skip: !serialNo || !auth?.accessToken, refetchOnMountOrArgChange: true }
  );

  useEffect(() => {
    if (cmdError) {
      logger.warn("[RemoteControl] 2.12 komut listesi alınamadı:", cmdError);
    }
  }, [cmdError]);

  const askConfirm = (fn, mode) => {
    const command = mode === "lock" ? fn.lock : fn.unlock;
    setConfirm({
      command,
      title: mode === "lock" ? `${fn.label} — Kilitle` : `${fn.label} — Aç`,
      message:
        mode === "lock"
          ? `"${fn.label}" fonksiyonu cihazda kilitlenecek. Onaylıyor musunuz?`
          : `"${fn.label}" fonksiyonunun kilidi açılacak. Onaylıyor musunuz?`,
    });
  };

  const handleSend = async () => {
    if (!confirm || !serialNo) return;
    const { command } = confirm;
    setPendingCommand(command);
    try {
      await sendCommand({
        token: auth.accessToken,
        serialNo,
        command,
      }).unwrap();
      showSuccess("Komut cihaza gönderildi.");
    } catch (err) {
      showError(
        err?.data?.message ||
          err?.data?.error ||
          "Komut gönderilemedi, lütfen tekrar deneyin."
      );
    } finally {
      setPendingCommand(null);
      setConfirm(null);
    }
  };

  return (
    <div className="h-full overflow-y-auto p-3 sm:p-4 bg-gray-50/40">
      {/* Bilgilendirme */}
      <div className="flex items-start gap-2 mb-4 p-3 rounded-xl bg-primary/10 border border-primary/30 text-sm text-main">
        <FaInfoCircle className="w-4 h-4 mt-0.5 text-primary shrink-0" />
        <p>
          Bu komutlar Espresso cihazına bulut platformu üzerinden gönderilir.
          Cihazın <span className="font-semibold text-fifth">mevcut kilit
          durumu</span> görüntülenemez; her fonksiyon için Kilitle veya Aç
          komutunu ayrı gönderirsiniz.
        </p>
      </div>

      {!serialNo && (
        <div className="mb-4 p-3 rounded-xl bg-newtwo border border-fifth/30 text-sm text-fifth">
          Cihazın seri numarası bulunamadı — komut gönderilemez.
        </div>
      )}

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        {FUNCTIONS.map((fn) => {
          const Icon = fn.icon;
          const lockBusy = pendingCommand === fn.lock;
          const unlockBusy = pendingCommand === fn.unlock;
          const disabled = !serialNo || isLoading;
          return (
            <div
              key={fn.key}
              className="bg-white rounded-2xl border border-gray-200 shadow-sm p-4 flex flex-col"
            >
              <div className="flex items-center gap-3 mb-1">
                <div className="p-2 rounded-xl bg-main/5">
                  <Icon className="w-5 h-5 text-main" />
                </div>
                <h4 className="text-base font-semibold text-gray-800">
                  {fn.label}
                </h4>
              </div>
              <p className="text-xs text-gray-500 mb-4">{fn.description}</p>

              <div className="mt-auto flex gap-2">
                <button
                  type="button"
                  disabled={disabled}
                  onClick={() => askConfirm(fn, "lock")}
                  className="flex-1 inline-flex items-center justify-center gap-2 px-3 py-2.5 rounded-xl bg-fifth text-white font-medium hover:opacity-90 transition-opacity disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {lockBusy ? (
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  ) : (
                    <FaLock className="w-4 h-4" />
                  )}
                  <span>Kilitle</span>
                </button>
                <button
                  type="button"
                  disabled={disabled}
                  onClick={() => askConfirm(fn, "unlock")}
                  className="flex-1 inline-flex items-center justify-center gap-2 px-3 py-2.5 rounded-xl bg-sixth text-white font-medium hover:opacity-90 transition-opacity disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {unlockBusy ? (
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  ) : (
                    <FaLockOpen className="w-4 h-4" />
                  )}
                  <span>Aç</span>
                </button>
              </div>
            </div>
          );
        })}
      </div>

      {/* Onay modali */}
      <Modal open={!!confirm} onClose={() => !pendingCommand && setConfirm(null)} maxWidth="sm">
        <Modal.Header
          title={confirm?.title}
          onClose={() => !pendingCommand && setConfirm(null)}
        />
        <Modal.Body>
          <p className="text-sm text-gray-600">{confirm?.message}</p>
        </Modal.Body>
        <Modal.Footer className="flex justify-end gap-2">
          <button
            type="button"
            disabled={!!pendingCommand}
            onClick={() => setConfirm(null)}
            className="px-4 py-2 rounded-xl bg-gray-100 text-gray-700 font-medium hover:bg-gray-200 transition-colors disabled:opacity-50"
          >
            İptal
          </button>
          <button
            type="button"
            disabled={!!pendingCommand}
            onClick={handleSend}
            className="inline-flex items-center justify-center gap-2 px-4 py-2 rounded-xl bg-main text-white font-medium hover:opacity-90 transition-opacity disabled:opacity-50"
          >
            {pendingCommand ? (
              <>
                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                <span>Gönderiliyor...</span>
              </>
            ) : (
              <span>Onayla</span>
            )}
          </button>
        </Modal.Footer>
      </Modal>
    </div>
  );
}
